import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://templeofsound.co.uk';
const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_cymbal-quoter/artifacts/gzy8k02q_Origin%20Brochure%20MAIN.jpeg';

// SEO Configuration for each page
export const SEO_CONFIG = {
  home: {
    title: 'Temple of Sound: Premium Handcrafted Cymbals & Drumsticks UK',
    description: "Temple of Sound: premium handcrafted cymbals and drumsticks from London Drumstick Co. and Alchemist Cymbals. Shop premium gear for artists and retailers.",
    keywords: 'Temple of Sound, British drumsticks, B20 cymbals, UK drum distributor, London Drumstick Company, Alchemist Cymbals, professional drum gear UK, wholesale drumsticks UK, wholesale cymbals UK',
    path: '/',
  },
  
  cymbals: {
    title: 'B20 Hand-Hammered Cymbals UK | Alchemist Cymbals | Professional Cymbals',
    description: 'Discover Alchemist Cymbals - premium B20 bronze hand-hammered cymbals crafted for professional drummers. Dark, dry, complex tones. Ride cymbals, crash cymbals, hi-hats and effects. Free UK shipping.',
    keywords: 'B20 cymbals, hand hammered cymbals, Alchemist Cymbals, professional cymbals, ride cymbals UK, crash cymbals UK, hi hats UK, dark cymbals, dry cymbals, bronze cymbals, artisan cymbals, cymbals for drummers',
    path: '/shop',
    image: '/images/opt/alchemist_logo.webp',
  },
  
  drumsticks: {
    title: 'British Drumsticks UK | London Drumstick Company | Premium Drumsticks',
    description: 'London Drumstick Company - British-made premium drumsticks for professional drummers. Hickory & maple drumsticks, 5A, 5B, 2B sizes. Custom drumsticks and signature models available. Made in Britain.',
    keywords: 'British drumsticks, drumsticks UK, UK made drumsticks, London Drumstick Company, professional drumsticks, hickory drumsticks, maple drumsticks, 5A drumsticks, 5B drumsticks, 2B drumsticks, custom drumsticks UK',
    path: '/drumsticks',
    image: 'https://customer-assets.emergentagent.com/job_cymbal-quoter/artifacts/44y3q0j5_Text%20Logo%20Gold_noBG.png',
  },
  
  customDrumsticks: {
    title: 'Custom Drumsticks UK | Personalised Drumsticks | London Drumstick Company',
    description: 'Design your own custom drumsticks with London Drumstick Company. Choose wood type, tip shape, length and diameter. Personalised drumsticks for bands, merchandise and professional drummers. Made in Britain.',
    keywords: 'custom drumsticks UK, personalised drumsticks, branded drumsticks, logo printed drumsticks, custom printed drumsticks, band merchandise drumsticks, signature drumsticks, drumstick customizer',
    path: '/custom-drumsticks',
  },
  
  artists: {
    title: 'Temple of Sound Artists | Endorsed Drummers | Cymbal & Drumstick Artists',
    description: 'Meet the professional drummers who play London Drumstick Company drumsticks and Alchemist Cymbals. Artist endorsement programme for drummers seeking quality British drum gear.',
    keywords: 'drum artists, endorsed drummers, cymbal artists, drumstick artists, Temple of Sound artists, London Drumstick Company artists, Alchemist Cymbals artists, drum endorsements, drummer sponsorship UK',
    path: '/artists',
  },
  
  checkout: {
    title: 'Checkout | Temple of Sound',
    description: 'Complete your order for British drumsticks and B20 cymbals. Secure checkout with free UK shipping on orders over £50.',
    keywords: 'buy drumsticks online, buy cymbals online, drum gear checkout',
    path: '/checkout',
    noIndex: true,
  },
  
  account: {
    title: 'My Account | Temple of Sound',
    description: 'Access your Temple of Sound account. View orders, manage Gold Dust and Beat Tokens loyalty points, and track deliveries.',
    keywords: 'Temple of Sound account, order history, loyalty points',
    path: '/customer/auth',
    noIndex: true,
  },
  
  b2b: {
    title: 'Wholesale Drumsticks & Cymbals for UK Retailers | Temple of Sound Trade',
    description: 'Become a stockist of London Drumstick Company and Alchemist Cymbals. Wholesale drum products with competitive dealer margins. UK music retail supplier.',
    keywords: 'wholesale drumsticks UK, wholesale cymbals UK, drum wholesale UK, drumstick distributor UK, cymbal distributor UK, become a stockist, drum retailer supply, music shop suppliers UK',
    path: '/login',
  },
  
  manager: {
    title: 'Manager Dashboard | Temple of Sound',
    description: 'Temple of Sound internal manager dashboard for order management, CRM, and store operations.',
    keywords: '',
    path: '/manager',
    noIndex: true,
  },
};

// Generate Artist Schema for individual artist pages
export const generateArtistSchema = (artist) => {
  // Collect social media links
  const sameAs = [
    artist.instagram_url,
    artist.facebook_url,
    artist.twitter_url,
    artist.youtube_url,
    artist.website_url
  ].filter(Boolean);
  
  const primaryImage = artist.photos?.[0]?.url || artist.gallery_photos?.[0]?.url;
  
  // Determine endorsement brands
  const endorsedBrands = [];
  if (artist.artist_type === 'both' || artist.artist_type === 'alchemist') {
    endorsedBrands.push({
      '@type': 'Brand',
      name: 'Alchemist Cymbals',
      url: 'https://templeofsound.co.uk/shop'
    });
  }
  if (artist.artist_type === 'both' || artist.artist_type === 'ldc') {
    endorsedBrands.push({
      '@type': 'Brand',
      name: 'London Drumstick Company',
      url: 'https://templeofsound.co.uk/drumsticks'
    });
  }

  // Main Person schema
  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `https://templeofsound.co.uk/artists/${artist.slug}#person`,
    name: artist.full_name,
    givenName: artist.full_name?.split(' ')[0],
    familyName: artist.full_name?.split(' ').slice(1).join(' ') || undefined,
    description: artist.biography || `${artist.full_name} is a professional drummer${artist.band_name ? ` with ${artist.band_name}` : ''} who plays ${
      artist.artist_type === 'both' ? 'London Drumstick Company drumsticks and Alchemist Cymbals' :
      artist.artist_type === 'ldc' ? 'London Drumstick Company drumsticks' :
      'Alchemist Cymbals'
    }. Endorsed artist at Temple of Sound.`,
    image: primaryImage ? {
      '@type': 'ImageObject',
      url: primaryImage,
      caption: `${artist.full_name}${artist.band_name ? ` - ${artist.band_name}` : ''}`
    } : undefined,
    url: `https://templeofsound.co.uk/artists/${artist.slug}`,
    sameAs: sameAs.length > 0 ? sameAs : undefined,
    jobTitle: 'Professional Drummer',
    hasOccupation: {
      '@type': 'Occupation',
      name: 'Drummer',
      occupationLocation: {
        '@type': 'Country',
        name: 'United Kingdom'
      }
    },
    knowsAbout: [
      'Drumming',
      'Percussion',
      'Live Performance',
      'Recording',
      ...(artist.artist_type === 'both' || artist.artist_type === 'ldc' ? ['Drumsticks', 'Drum Sticks'] : []),
      ...(artist.artist_type === 'both' || artist.artist_type === 'alchemist' ? ['Cymbals', 'B20 Cymbals'] : [])
    ],
    // Brand endorsements
    sponsor: endorsedBrands.length > 0 ? endorsedBrands : undefined,
  };

  // Add band membership if applicable
  if (artist.band_name) {
    personSchema.memberOf = {
      '@type': 'MusicGroup',
      '@id': `https://templeofsound.co.uk/artists/${artist.slug}#band`,
      name: artist.band_name,
      member: {
        '@type': 'OrganizationRole',
        member: { '@id': `https://templeofsound.co.uk/artists/${artist.slug}#person` },
        roleName: 'Drummer'
      }
    };
    personSchema.performerIn = {
      '@type': 'MusicGroup',
      name: artist.band_name
    };
  }

  return personSchema;
};

// Generate SEO props for individual artist pages  
export const generateArtistSEO = (artist) => {
  const brandText = artist.artist_type === 'both' 
    ? 'London Drumstick Company & Alchemist Cymbals' 
    : artist.artist_type === 'ldc' 
      ? 'London Drumstick Company' 
      : 'Alchemist Cymbals';
  
  const brandShort = artist.artist_type === 'both' 
    ? 'LDC & Alchemist' 
    : artist.artist_type === 'ldc' 
      ? 'LDC' 
      : 'Alchemist';
  
  // Build SEO-optimized title: "Artist Name - Band | Drummer | Brand Artist"
  let title = artist.full_name;
  if (artist.band_name) {
    title += ` - ${artist.band_name} Drummer`;
  } else {
    title += ' | Professional Drummer';
  }
  title += ` | ${brandShort} Artist`;

  // Build description with key terms
  let description;
  if (artist.biography && artist.biography.length > 50) {
    // Use biography but ensure it mentions key terms
    description = artist.biography.substring(0, 140);
    if (!description.toLowerCase().includes('drummer')) {
      description += ' Professional drummer';
    }
    if (artist.band_name && !description.includes(artist.band_name)) {
      description += ` with ${artist.band_name}`;
    }
    description += `. ${brandText} endorsed artist.`;
    if (description.length > 160) {
      description = description.substring(0, 157) + '...';
    }
  } else {
    description = `${artist.full_name} is a professional drummer${artist.band_name ? ` with ${artist.band_name}` : ''} endorsed by ${brandText}. Part of the Temple of Sound artist family. View profile, photos and gear.`;
  }

  // Build comprehensive keywords
  const keywordParts = [
    artist.full_name,
    `${artist.full_name} drummer`,
    artist.band_name,
    artist.band_name ? `${artist.band_name} drummer` : null,
    artist.band_name ? `${artist.band_name} band` : null,
    `${brandText} artist`,
    'endorsed drummer',
    'professional drummer',
    'drum artist UK',
    'Temple of Sound artist',
    artist.artist_type === 'alchemist' || artist.artist_type === 'both' ? 'cymbal artist' : null,
    artist.artist_type === 'ldc' || artist.artist_type === 'both' ? 'drumstick artist' : null,
  ].filter(Boolean);

  return {
    title,
    description,
    keywords: keywordParts.join(', '),
    image: artist.photos?.[0]?.url || artist.gallery_photos?.[0]?.url,
    path: `/artists/${artist.slug}`,
    schema: generateArtistSchema(artist),
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Artists', path: '/artists' },
      { name: artist.full_name, path: `/artists/${artist.slug}` }
    ]
  };
};

// Generate Product Schema for individual products
export const generateProductSchema = (product) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: product.name,
  description: product.description,
  image: product.image,
  brand: {
    '@type': 'Brand',
    name: product.brand,
  },
  offers: {
    '@type': 'Offer',
    price: product.price,
    priceCurrency: 'GBP',
    availability: 'https://schema.org/InStock',
    seller: {
      '@type': 'Organization',
      name: 'Temple of Sound',
    },
  },
  category: product.category,
});

// Generate ItemList schema with Product entries (price + stock) for shop/category pages
export const generateProductListSchema = ({ products, brand, path, currency = 'GBP' }) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  numberOfItems: products.length,
  itemListElement: products.map((p, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    item: {
      '@type': 'Product',
      name: p.name,
      sku: p.sku,
      ...(p.image ? { image: p.image } : {}),
      brand: { '@type': 'Brand', name: brand },
      offers: {
        '@type': 'Offer',
        price: Number(p.price || 0).toFixed(2),
        priceCurrency: currency,
        availability: p.inStock === false ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        url: `${BASE_URL}${path}#${encodeURIComponent(p.sku)}`,
        seller: { '@type': 'Organization', name: 'Temple of Sound' },
      },
    },
  })),
});

// Generate BreadcrumbList Schema
export const generateBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `${BASE_URL}${item.path}`,
  })),
});

// Main SEO Component
const SEO = ({ 
  page = 'home',
  title,
  description,
  keywords,
  image,
  noIndex = false,
  schema,
  breadcrumbs,
}) => {
  const config = SEO_CONFIG[page] || SEO_CONFIG.home;
  
  const seoTitle = title || config.title;
  const seoDescription = description || config.description;
  const seoKeywords = keywords || config.keywords;
  const rawImage = image || config.image || DEFAULT_IMAGE;
  const seoImage = rawImage.startsWith('http') ? rawImage : `${BASE_URL}${rawImage}`;
  const seoPath = config.path || '/';
  const shouldNoIndex = noIndex || config.noIndex;
  
  const canonicalUrl = `${BASE_URL}${seoPath}`;
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      {shouldNoIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large" />
      )}
      
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:site_name" content="Temple of Sound" />
      <meta property="og:locale" content="en_GB" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      
      {/* Structured Data */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
      
      {/* Breadcrumbs Schema */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify(generateBreadcrumbSchema(breadcrumbs))}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
